import { ReactNode, useCallback, useMemo } from "react";
import { useSellarDistributionAvailabilityDistributionDeliveryEstimateTimetableList } from "~/hooks/api/v2/sellarDistributionAvailability/useSellarDistributionAvailabilityDistributionDeliveryTimetableList";
import { useSellarCurrentBusiness } from "~/hooks/useSellarCurrentBusiness.shared";
import { BuyerRelationshipDeliveryEstimateContext } from "./BuyerRelationshipDeliveryEstimateContext";

type BuyerRelationshipDeliveryEstimateProviderProps = {
  children: ReactNode;
  buyerRelationshipSupplierIdsByPage: number[][];
};

export const BuyerRelationshipDeliveryEstimateProvider = ({
  children,
  buyerRelationshipSupplierIdsByPage,
}: BuyerRelationshipDeliveryEstimateProviderProps) => {
  const { business } = useSellarCurrentBusiness();

  const buyerPostcode = business?.DeliveryAddress.postalCode ?? "";

  const supplierIdsByPage = useMemo(
    () =>
      buyerRelationshipSupplierIdsByPage.map((supplierIds) => [
        ...new Set(supplierIds),
      ]),
    [buyerRelationshipSupplierIdsByPage],
  );

  const deliveryEstimatesQuery =
    useSellarDistributionAvailabilityDistributionDeliveryEstimateTimetableList({
      supplierIdsByPage,
      buyerPostcode,
      enabled: Boolean(buyerPostcode) && supplierIdsByPage.flat().length > 0,
    });

  const getDistributionDeliveryEstimate = useCallback(
    (supplierId: number) => {
      return deliveryEstimatesQuery.data.find(
        (estimate) => estimate.SupplierId === supplierId,
      );
    },
    [deliveryEstimatesQuery.data],
  );

  const value = useMemo(() => {
    return {
      deliveryEstimates: deliveryEstimatesQuery.data,
      getDistributionDeliveryEstimate,
    };
  }, [deliveryEstimatesQuery.data, getDistributionDeliveryEstimate]);

  return (
    <BuyerRelationshipDeliveryEstimateContext.Provider value={value}>
      {children}
    </BuyerRelationshipDeliveryEstimateContext.Provider>
  );
};
